// components/profile/components/ImageUpload.jsx
import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Typography, Spinner, IconButton, Button } from '@material-tailwind/react';
import {
  XMarkIcon,
  CloudArrowUpIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import Cropper from 'react-easy-crop';
import { uploadImage } from '../../../services/jeweller';

// Helper function to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

// Helper function to get cropped image
async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

export const ImageUpload = ({
  value,
  onChange,
  label = "Upload Image",
  maxSize = 5,
  disabled = false,
  maxFiles = 1,
  aspectRatio = null, // Set to "square" for 1:1 or "header" for 2:1
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // --- State to manage the fullscreen image preview ---
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // --- State for image cropping ---
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const images = Array.isArray(value) ? value : (value ? [value] : []);

  // Get the numeric aspect ratio based on the prop
  const getCropAspectRatio = () => {
    if (aspectRatio === 'square') return 1; // 1:1
    if (aspectRatio === 'header') return 3; // Change from 2 to 3 for 3:1
    return null; // Free aspect ratio
  };

  // Get preview dimensions based on aspect ratio
  const getPreviewDimensions = () => {
    if (aspectRatio === 'header') {
      return { width: 'w-96', height: 'h-32' }; // Change from h-48 to h-32 for 3:1 ratio
    }
    // Default square for both 'square' and null
    return { width: 'w-48', height: 'h-48' }; // 1:1 ratio
  };

  // Get upload box dimensions based on aspect ratio
  const getUploadBoxDimensions = () => {
    if (aspectRatio === 'header') {
      return { width: 'w-96', height: 'h-32' }; // Change from h-48 to h-32 for 3:1 ratio
    }
    return { width: 'w-48', height: 'h-48' }; // 1:1 ratio
  };

  // Get the crop shape (for future flexibility)
  const getCropShape = () => {
    return 'rect'; // Always rectangular, but aspect ratio changes
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = async () => {
    try {
      setIsUploading(true);
      setCropModalOpen(false);
      
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      
      const url = await uploadImage(file);
      if (maxFiles === 1) {
        onChange(url);
      } else {
        onChange([...images, url]);
      }
      
      // Reset states
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleFile = async (file) => {
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file.');
      return;
    }
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB.`);
      return;
    }
    
    // If aspect ratio is required, show crop modal
    if (aspectRatio === 'square' || aspectRatio === 'header') {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        // Small delay to prevent React rendering issues
        setTimeout(() => {
          setCropModalOpen(true);
        }, 50);
      };
    } else {
      // Original upload logic without cropping
      try {
        setIsUploading(true);
        const url = await uploadImage(file);
        if (maxFiles === 1) {
          onChange(url);
        } else {
          onChange([...images, url]);
        }
      } catch (err) {
        setError('Failed to upload image. Please try again.');
        console.error('Upload error:', err);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = (index) => {
    if (maxFiles === 1) {
      onChange('');
    } else {
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    }
  };

  // Get display text based on aspect ratio
  const getDisplayText = () => {
    if (aspectRatio === 'square') return 'Add Square Photo';
    if (aspectRatio === 'header') return 'Add Header Image (3:1)'; // Change from 2:1 to 3:1
    return 'Add Photo';
  };

  const previewDimensions = getPreviewDimensions();
  const uploadBoxDimensions = getUploadBoxDimensions();

  return (
    <>
      <div className="w-full text-center">
        <div className="flex flex-wrap gap-4 justify-center">
          {images.map((url, index) => (
            <div
              key={index}
              className={`relative ${previewDimensions.height} ${previewDimensions.width} rounded-lg overflow-hidden cursor-pointer group`}
              onClick={() => setFullscreenImage(url)}
            >
              <img
                src={url}
                alt={`upload-preview-${index}`}
                className="h-full w-full object-cover"
              />
              {/* Trash icon overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 flex items-center justify-center transition-all duration-300">
                <IconButton
                  variant="text"
                  className="bg-white/80 hover:bg-white rounded-full opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(index);
                  }}
                >
                  <TrashIcon className="h-5 w-5 text-red-500" />
                </IconButton>
              </div>
            </div>
          ))}

          {images.length < maxFiles && !isUploading && (
            <div
              className={`
                relative ${uploadBoxDimensions.height} ${uploadBoxDimensions.width} rounded-lg border-2 border-dashed
                flex flex-col items-center justify-center
                ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
              `}
              onClick={() => !disabled && fileInputRef.current?.click()}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <PlusIcon className="h-8 w-8 text-gray-400" />
              <Typography variant="small" className="text-gray-600 mt-2 text-center">
                {getDisplayText()}
              </Typography>
            </div>
          )}

          {isUploading && (
            <div className={`${uploadBoxDimensions.height} ${uploadBoxDimensions.width} rounded-lg bg-gray-100 flex flex-col items-center justify-center`}>
              <Spinner className="h-8 w-8 mb-2" />
              <Typography variant="small">Uploading...</Typography>
            </div>
          )}
        </div>

        <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleFileChange} disabled={disabled || isUploading} />

        {error && <Typography variant="small" className="text-red-500 mt-2 text-center">{error}</Typography>}
      </div>

      {/* Render crop modal using portal - only when open */}
      {cropModalOpen && imageToCrop && createPortal(
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-2xl"
            style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <Typography variant="h6">Crop Your Image</Typography>
              <Typography variant="small" className="text-gray-600">
                {aspectRatio === 'header' ? 'Adjust to 2:1 ratio for header' : 'Drag to position, use slider to zoom'}
              </Typography>
            </div>
            
            {/* Cropper Container */}
            <div className="flex-1 relative bg-gray-100" style={{ minHeight: '400px' }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={getCropAspectRatio()}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                minZoom={0.5}
                maxZoom={3}
                cropShape={getCropShape()}
                showGrid={true}
                restrictPosition={true}
                zoomSpeed={0.1}
              />
            </div>
            
            {/* Zoom Slider */}
            <div className="px-6 py-3 border-t bg-white">
              <div className="flex items-center gap-4">
                <Typography variant="small" className="text-gray-600 min-w-fit">Zoom:</Typography>
                <input
                  type="range"
                  value={zoom}
                  min={0.5}
                  max={3}
                  step={0.01}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <Typography variant="small" className="text-gray-500 min-w-fit">
                  {Math.round(zoom * 100)}%
                </Typography>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="p-4 border-t flex justify-end gap-3 bg-white">
              <Button
                variant="outlined"
                onClick={handleCropCancel}
                className="flex items-center gap-2"
              >
                <XMarkIcon className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={handleCropConfirm}
                disabled={!croppedAreaPixels}
                className="flex items-center gap-2"
                style={{ 
                  background: 'var(--brand-gradient-1, #3b82f6)',
                  color: 'white'
                }}
              >
                <CheckIcon className="h-4 w-4" />
                Crop & Upload
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Render lightbox using portal */}
      <ImageLightbox 
        imageUrl={fullscreenImage} 
        onClose={() => setFullscreenImage(null)}
      />
    </>
  );
};

// Simple lightbox component for fullscreen image preview
const ImageLightbox = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-[10000]"
      onClick={onClose}
      style={{ cursor: 'zoom-out' }}
    >
      <img 
        src={imageUrl} 
        alt="Full size preview" 
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300"
      >
        <XMarkIcon className="h-8 w-8" />
      </button>
    </div>,
    document.body
  );
};